import { EmptyState, VAccordion } from '@vrzl/ui';
import { useAtomValue } from 'jotai';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CRITERIA_SEARCH_EMPTY_TITLE, CriteriaSearch, useCriteriaSearch } from '@/domains/criteria';

import type { ValidationReport, ValidationReportItem } from '../api/types';
import { printModeAtom } from '../state/printMode.atom';
import {
  buildGroupViews,
  countValidation,
  updateClosedKeys,
  type ValidationFilter,
} from '../utils/validationView';

import { ValidationFilterBar } from './validationFilterBar';
import { ValidationGroup } from './validationGroup';
import { ValidationSummary } from './validationSummary';

const getName = (item: ValidationReportItem) => item.name;
const getValues = (item: ValidationReportItem) => item.answers.map((answer) => answer.value);

interface ValidationResultProps {
  report: ValidationReport;
  eventId: string;
  activeCriteria: number;
  totalCriteria: number;
}

export function ValidationResult({
  report,
  eventId,
  activeCriteria,
  totalCriteria,
}: ValidationResultProps) {
  const printMode = useAtomValue(printModeAtom);
  // Niet bewaard: bij terugkomen staat het filter weer op 'Alles'.
  const [filter, setFilter] = useState<ValidationFilter>('all');
  // Alle onderwerpen starten open; we onthouden alleen wat de gebruiker dichtklapt.
  const [closedKeys, setClosedKeys] = useState<ReadonlySet<string>>(new Set());
  const [sentFeedback, setSentFeedback] = useState<ReadonlySet<string>>(new Set());
  const markFeedbackSent = (criterionId: string) =>
    setSentFeedback((prev) => new Set(prev).add(criterionId));

  const { query, setQuery, results, isSearching } = useCriteriaSearch(report.items, {
    getName,
    getValues,
  });

  // Export toont altijd het volledige rapport: 'Alles', zonder zoekterm, alles open.
  const activeFilter = printMode ? 'all' : filter;
  const visibleItems = printMode ? report.items : results;

  const counts = useMemo(() => countValidation(report.items), [report.items]);
  const groups = useMemo(
    () => buildGroupViews(report.items, visibleItems, activeFilter),
    [report.items, visibleItems, activeFilter],
  );
  const groupKeys = groups.map((group) => group.key);
  const openKeys = printMode ? groupKeys : groupKeys.filter((key) => !closedKeys.has(key));

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <ValidationFilterBar value={filter} counts={counts} onChange={setFilter} />
        <CriteriaSearch value={query} onChange={setQuery} className="w-full max-w-xs" />
      </div>

      <ValidationSummary
        filter={activeFilter}
        counts={counts}
        activeCriteria={activeCriteria}
        totalCriteria={totalCriteria}
      />

      {groups.length > 0 ? (
        <VAccordion
          type="multiple"
          value={openKeys}
          onValueChange={(next) => setClosedKeys((prev) => updateClosedKeys(prev, groupKeys, next))}
          className="space-y-3"
        >
          {groups.map((view) => (
            <ValidationGroup
              key={view.key}
              view={view}
              filter={activeFilter}
              eventId={eventId}
              sentFeedback={sentFeedback}
              onFeedbackSent={markFeedbackSent}
            />
          ))}
        </VAccordion>
      ) : isSearching && results.length === 0 ? (
        <EmptyState
          icon={Search}
          title={CRITERIA_SEARCH_EMPTY_TITLE}
          description="Pas je zoekterm aan om andere criteria te zien."
        />
      ) : (
        <EmptyState
          icon={Search}
          title="Geen criteria voor dit filter"
          description={
            isSearching
              ? "Geen van de zoekresultaten valt binnen dit filter. Kies 'Alles' om ze allemaal te zien."
              : "Er zijn geen criteria die aan dit filter voldoen. Kies 'Alles' om alle records weer te zien."
          }
        />
      )}
    </div>
  );
}
